import React, { useEffect, useRef } from 'react';
import { CloseOrigin, ToastData, ToastTypes } from './model.ts';
import { getKeys } from '../../../../_helpers/keyboard.ts';
import { joinString } from '../../../../_helpers/join-string.ts';

import './Toast.scss';

type ToastProps = {
	toastData: Omit<ToastData, 'onClose'>;
	onClose?: (closeOrigin: CloseOrigin) => void;
	className?: string;
};

const Toast: React.FC<ToastProps> = ({ toastData, onClose, className }) => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (toastData.focused) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			ref.current.focus();
		}
	}, [toastData]);

	const onToastKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
		const keys = getKeys(event);

		if (keys.isEscape) {
			onClose?.(CloseOrigin.MANUAL);
		}
	};

	const onToastKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		const keys = getKeys(event);

		if (keys.isShiftTab && event.target === ref.current) {
			event.preventDefault();
			toastData.focusPrev?.();
		}
	};

	const onCtaKeyUp: React.KeyboardEventHandler<HTMLButtonElement> = (event) => {
		const keys = getKeys(event);

		if (keys.isEnter || keys.isSpace) {
			event.preventDefault();
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			toastData.ctaCallback(() => onClose?.(CloseOrigin.CTA_CALLBACK));
		}
	};

	const onCloseBtnKeyUp = (event: React.KeyboardEvent<HTMLElement>) => {
		const keys = getKeys(event);

		if (keys.isEnter || keys.isSpace) {
			event.preventDefault();
			onClose?.(CloseOrigin.MANUAL);
		}
	};

	const onCloseBtnKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
		const keys = getKeys(event);

		if (keys.isTab) {
			event.preventDefault();
			toastData.focusNext?.();
		}
	};

	const getIconClass = (type: ToastTypes) => {
		switch (type) {
			case ToastTypes.Success:
				return 'fas fa-check-circle';
			case ToastTypes.Error:
				return 'fas fa-exclamation-circle';
			case ToastTypes.Warning:
				return 'fas fa-exclamation-triangle';
			default:
				return 'fas fa-exclamation-circle';
		}
	};

	return (
		<div
			data-testid="toaster"
			className={joinString(['blk-ac-toast', toastData.type, className])}
			aria-label={toastData.ariaLabel}
			role={'alert'}
			aria-live={'polite'}
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			tabIndex={toastData.focused ? 0 : null}
			ref={ref}
			onKeyUp={onToastKeyUp}
			onKeyDown={onToastKeyDown}
		>
			<div className="blk-ac-toast-container">
				<i className={`icon ${getIconClass(toastData.type)}`}></i>
				<div className="blk-ac-toast-content">
					<p className="text blk-ac-body-m">{toastData.label}</p>

					{toastData.ctaLabel && toastData.ctaCallback && (
						<button
							data-testid="toaster-cta-button"
							className="cta-button blk-ac-body-m-bold"
							onClick={() =>
								// eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore
								toastData.ctaCallback(() => onClose?.(CloseOrigin.CTA_CALLBACK))
							}
							onKeyUp={onCtaKeyUp}
							data-link-event={toastData.ctaDataLinkEvent}
							aria-label={toastData.ctaAriaLabel}
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							tabIndex={toastData.focused ? 0 : null}
						>
							{toastData.ctaLabel}
						</button>
					)}
				</div>
			</div>
			<i
				data-testid="toaster-close-button"
				aria-label={toastData.closeButtonAriaLabel}
				className="close fas fa-times"
				onClick={() => onClose?.(CloseOrigin.MANUAL)}
				onKeyUp={onCloseBtnKeyUp}
				onKeyDown={onCloseBtnKeyDown}
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				tabIndex={toastData.focused ? 0 : null}
			></i>
		</div>
	);
};

export default Toast;
